// Party-by-email resolution for the person popover (architecture/MAIL.md
// §3.7 Directory row — "resolve by email against Party emails and members;
// 'Add to directory' creates a Party"). Members resolve through findPerson
// already for their OWN address; this file covers every address findPerson
// cannot answer — an outside Party contact, or a colleague writing from a
// personal alias findPerson never sees (it only knows canonical addresses).
//
// The cache is root._partyByEmail: absent = never asked, `false` = resolved
// with NO match (the Add-to-directory gate), an object = the match summary.
// resolvePartyByEmail reads GET /core/mail/resolve (MailService#resolveAddress,
// MAIL-INTEGRATIONS-SERVER-1) — ONE server read against Party emails,
// WorkspaceMember addresses AND UserEmailAlias rows in one pass, replacing
// the old parties.list({q}) client-side scan that could never see an alias.
import { getSDK } from '@symbo-ls/workspace-shared/functions/sdk.js'
import { sameWorkspaceWriter, wsIdOf } from './sameWorkspaceWriter.js'

const _shellNorm = (email) =>
  String(email || '')
    .trim()
    .toLowerCase()

const _shellSummary = (row) => ({
  id: String(row.id || row._id || ''),
  name: row.name || '',
  kind: row.kind || 'person'
})

// The resolver answers `{ email, member, party, alias }`. A member match
// beats a party match — an active colleague outranks a directory stub at the
// same address — and `alias` alone (a known user with no seat here and no
// directory Party) carries no local identity to show, so it stays a
// non-match, same as a genuine stranger.
const _summaryFromResolve = (res) => {
  if (res && res.member) {
    return { id: String(res.member.userId || ''), name: res.member.name || '', kind: 'person' }
  }
  if (res && res.party) return _shellSummary(res.party)
  return false
}

// One in-flight lookup per address — a popover re-render must not fan out
// duplicate resolve calls (the chatFold single-flight idiom).
const _inFlight = new Map()

export const resolvePartyByEmail = async function (email) {
  const root = this && this.state && this.state.root
  const key = _shellNorm(email)
  if (!root || !key) return null
  const cached = (root._partyByEmail || {})[key]
  if (cached !== undefined) return cached
  if (_inFlight.has(key)) return _inFlight.get(key)
  const write = sameWorkspaceWriter(root)
  const run = (async () => {
    try {
      const mail = await getSDK().getService('mail')
      const res = await mail.resolveAddress(key, { workspaceId: wsIdOf(root) })
      const value = _summaryFromResolve(res)
      write({ _partyByEmail: { ...(root._partyByEmail || {}), [key]: value } })
      return value
    } catch (err) {
      // Leave the cache UNTOUCHED on a failed read: absent means "never
      // resolved", so the next open retries instead of freezing a guess.
      console.warn('[partyLookup] resolve failed', err)
      return null
    } finally {
      _inFlight.delete(key)
    }
  })()
  _inFlight.set(key, run)
  return run
}

// "Add to directory" — creates the person Party carrying this address. The
// display name (when the caller knows one — a mail sender header) beats the
// bare address as the party name; directory UI renames land later as usual.
export const addPartyFromEmail = async function (email, displayName) {
  const root = this && this.state && this.state.root
  const key = _shellNorm(email)
  if (!root || !key) return null
  const write = sameWorkspaceWriter(root)
  try {
    const parties = await getSDK().getService('parties')
    const row = await parties.create({
      kind: 'person',
      name: (displayName || '').trim() || key,
      emails: [key]
    })
    const value = row ? _shellSummary(row) : false
    if (value) write({ _partyByEmail: { ...(root._partyByEmail || {}), [key]: value } })
    this.call('openNotification', {
      type: 'success',
      icon: 'people',
      title: this.call('polyglot', 'workspace.person.addedDirectory'),
      message: (displayName || '').trim() || key
    })
    return value || null
  } catch (err) {
    this.call('openNotification', {
      type: 'error',
      title: this.call('polyglot', 'workspace.person.addDirectoryFailed'),
      message: (err && err.message) || ''
    })
    return null
  }
}
